/**
 * useTimezone Hook
 * 
 * Provides access to the user's timezone preference and date formatting utilities.
 * This hook automatically uses the user's saved timezone from their profile.
 * 
 * Usage:
 *   const { timezone, formatDate, formatDateTime, formatDateSmart } = useTimezone();
 *   
 *   // All dates will be formatted in the user's timezone
 *   <span>{formatDate(someDate)}</span>
 *   <span>{formatDateTime(someDate)}</span>
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  formatDate as formatDateUtil,
  formatDateTime as formatDateTimeUtil,
  formatDateTimeFull as formatDateTimeFullUtil,
  formatTime as formatTimeUtil,
  formatDateSmart as formatDateSmartUtil,
  formatRelativeTime as formatRelativeTimeUtil,
  getBrowserTimezone
} from '../utils/dateUtils';

export const useTimezone = () => {
  const { user } = useAuth();
  
  // Get the user's timezone, falling back to browser timezone, then UTC
  const timezone = useMemo(() => {
    return user?.timezone || getBrowserTimezone() || 'UTC';
  }, [user?.timezone]);
  
  // Memoized formatting functions that automatically use the user's timezone
  const formatDate = useCallback(
    (dateInput, options = {}) => formatDateUtil(dateInput, timezone, options),
    [timezone]
  );
  
  const formatDateTime = useCallback(
    (dateInput, options = {}) => formatDateTimeUtil(dateInput, timezone, options),
    [timezone]
  );
  
  const formatDateTimeFull = useCallback(
    (dateInput) => formatDateTimeFullUtil(dateInput, timezone),
    [timezone]
  );
  
  const formatTime = useCallback(
    (dateInput) => formatTimeUtil(dateInput, timezone),
    [timezone]
  );
  
  const formatDateSmart = useCallback(
    (dateInput) => formatDateSmartUtil(dateInput, timezone),
    [timezone]
  );
  
  // Relative time is timezone-agnostic (it's always relative to "now")
  const formatRelativeTime = useCallback(
    (dateInput, options = {}) => formatRelativeTimeUtil(dateInput, options),
    []
  );
  
  return {
    timezone,
    formatDate,
    formatDateTime,
    formatDateTimeFull,
    formatTime,
    formatDateSmart,
    formatRelativeTime
  };
};

export default useTimezone;
